/**
 * Chargement paresseux (code splitting) tolérant aux pannes.
 *
 * Contexte : sur Cloudflare Pages, la règle `/* /index.html 200` renvoie
 * index.html (HTTP 200, `text/html`) lorsqu'un chunk est absent : déploiement en
 * cours, hash obsolète (onglet ouvert pendant une publication) ou coupure
 * réseau. Webpack émet alors un `ChunkLoadError` et, sans garde-fou, tout le
 * rendu React reste blanc.
 *
 * Stratégie :
 *  1. tentative immédiate ;
 *  2. seconde tentative après un court délai (incident réseau passager) ;
 *  3. rechargement complet de la page, une seule fois par session et par chunk
 *     (garde-fou sessionStorage) afin d'éviter toute boucle de rechargement ;
 *  4. si l'échec persiste, l'erreur est propagée à l'ErrorBoundary de l'app.
 */

import { lazy } from 'react';

const RELOAD_GUARD_PREFIX = 'charcot:chunk-reload:';
const RETRY_DELAY_MS = 400;

function readReloadGuard(key) {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch (error) {
    // sessionStorage indisponible : on ne prend pas le risque d'une boucle.
    return true;
  }
}

function setReloadGuard(key) {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch (error) {
    /* sessionStorage indisponible : ignoré */
  }
}

function clearReloadGuard(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    /* sessionStorage indisponible : ignoré */
  }
}

function delay(ms) {
  return new Promise(resolve => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Équivalent de React.lazy, avec reprise sur erreur de chargement de chunk.
 * @param {Function} componentImport - `() => import('./pages/MaPage')`
 * @param {string} name - Identifiant du chunk, utilisé par le garde-fou de rechargement
 * @returns {React.LazyExoticComponent}
 */
export const lazyWithRetry = (componentImport, name = 'page') => {
  const guardKey = `${RELOAD_GUARD_PREFIX}${name}`;

  return lazy(() => {
    const load = () => componentImport();

    const retryThenReload = () =>
      delay(RETRY_DELAY_MS)
        .then(load)
        .catch(error => {
          if (!readReloadGuard(guardKey)) {
            setReloadGuard(guardKey);
            window.location.reload();
            // Promesse jamais résolue : le fallback de Suspense reste affiché
            // pendant le rechargement, aucun rendu en erreur n'est déclenché.
            return new Promise(() => {});
          }

          clearReloadGuard(guardKey);

          throw error;
        });

    return load()
      .then(module => {
        clearReloadGuard(guardKey);

        return module;
      })
      .catch(retryThenReload);
  });
};
